import { motion } from "framer-motion";
import { useState } from "react";
import { GoArrowLeft } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { logoutFunction } from "services/authRequests";
import { LoadingDots } from "utils/loadingAnimation";

interface LogoutPopUpProps {
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Definition of the variants of this component
 */
const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const LogoutPopUp: React.FC<LogoutPopUpProps> = ({ setShowPopUp }) => {
  // States definition
  const [isLoading, setIsLoading] = useState(false);

  // Navigate hook
  const navigate = useNavigate();

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    // Check if it's already loading
    if (isLoading) return;

    // Set the loading state
    setIsLoading(true);

    // Apply the logout
    await logoutFunction(navigate);

    // Release the loading
    setIsLoading(false);
  };

  /**
   * Handle
   */
  const handleBackgroundClick = () => {
    // Check if it's loading
    if (isLoading) return;

    // Otherwise, close the pop up
    setShowPopUp(false);
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-30 flex flex-col flex-grow"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleBackgroundClick}
      />
      <motion.div
        className="fixed w-[440px] h-fit left-0 right-0 ml-auto mr-auto bottom-4 p-6 bg-white rounded-3xl flex-col justify-start items-center gap-10 inline-flex z-30"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
      >
        <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
          <div className="self-stretch justify-center items-center gap-4 inline-flex">
            <button
              className="p-1.5 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex cursor-pointer hover:bg-neutral-100 ease-in-out duration-150"
              onClick={() => setShowPopUp(false)}
            >
              <div className="w-5 h-5 relative">
                <GoArrowLeft className="w-full h-full fill-secondary-600" />
              </div>
            </button>
            <div className="grow shrink basis-0 text-neutral-700 text-[23px] font-semibold font-sans leading-7">
              Logout?
            </div>
          </div>
          <div className="self-stretch justify-start items-start gap-2 inline-flex">
            <div className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-7">
              Are you sure you want to logout?
            </div>
          </div>
        </div>
        <button
          className={`self-stretch h-fit px-5 py-3 rounded border justify-center items-center gap-2 inline-flex ease-in-out duration-150 ${
            isLoading
              ? "bg-Subtle cursor-not-allowed"
              : "text-secondary-600 border-secondary-600 hover:bg-secondary-600 hover:text-white cursor-pointer"
          }`}
          onClick={handleLogout}
        >
          {isLoading ? (
            <LoadingDots bgColor="bg-neutral-400" />
          ) : (
            <div className="text-lg font-semibold font-sans leading-snug">
              Logout
            </div>
          )}
        </button>
      </motion.div>
    </>
  );
};

export default LogoutPopUp;
